import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import NavHeader from '@/components/NavHeader';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, ScrollText } from 'lucide-react';

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="mb-6" style={{ background: 'hsl(30 6% 7%)', borderColor: 'hsl(35 12% 14%)' }}>
    <CardContent className="p-8">
      <h2
        className="text-2xl font-semibold mb-4"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: 'hsl(38 65% 57%)',
          letterSpacing: '0.02em',
        }}
      >
        {title}
      </h2>
      <div className="text-sm leading-relaxed space-y-3" style={{ color: 'hsl(40 15% 72%)' }}>
        {children}
      </div>
    </CardContent>
  </Card>
);

export default function LicensePage() {
  return (
    <PageLayout>
      <NavHeader />
      <div className="container py-24 max-w-3xl">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-10 transition-colors hover:opacity-80"
          style={{ color: 'hsl(38 65% 57%)' }}
        >
          <ArrowLeft size={15} />
          Back to SelfWorth DApp
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <ScrollText size={22} style={{ color: 'hsl(38 65% 57%)' }} />
            <span
              className="text-sm font-medium tracking-widest uppercase"
              style={{ color: 'hsl(35 8% 52%)' }}
            >
              Legal
            </span>
          </div>
          <h1
            className="text-5xl font-semibold mb-3"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: 'hsl(40 15% 92%)',
              lineHeight: 1.15,
            }}
          >
            End-User License Agreement
          </h1>
          <p className="text-sm" style={{ color: 'hsl(35 8% 52%)' }}>
            SelfWorth DApp &nbsp;·&nbsp; Last updated: April 2026
          </p>
        </div>

        {/* Intro */}
        <p className="text-sm leading-relaxed mb-8" style={{ color: 'hsl(40 15% 72%)' }}>
          This End-User License Agreement ("EULA") is a legal agreement between you ("User") and the
          developers of SelfWorth DApp ("we", "us", or "Licensor"). By accessing or using SelfWorth
          DApp, you agree to be bound by the terms of this EULA. If you do not agree, do not use the
          App.
        </p>

        {/* Sections */}
        <SectionCard title="1. License Grant">
          <p>
            Subject to your compliance with this EULA, we grant you a limited, non-exclusive,
            non-transferable, revocable license to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Access and use SelfWorth DApp via supported web browsers and compatible wallets.</li>
            <li>
              Interact with smart contracts deployed on the Solana blockchain as facilitated by the
              App's interface.
            </li>
            <li>
              View and use the App's features — including token locking, leaderboards, prize pools,
              and donation tools — for personal, non-commercial purposes.
            </li>
          </ul>
          <p>
            This license is personal to you and may not be transferred, sublicensed, or assigned
            without our prior written consent.
          </p>
        </SectionCard>

        <SectionCard title="2. Restrictions">
          <p>You may not, and you agree not to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              Copy, modify, adapt, translate, reverse-engineer, decompile, or disassemble any
              portion of the App's frontend code or backend infrastructure, except as expressly
              permitted by applicable law.
            </li>
            <li>
              Use the App for any unlawful purpose or in violation of any applicable laws or
              regulations, including financial regulations in your jurisdiction.
            </li>
            <li>
              Attempt to gain unauthorized access to any systems, networks, or data associated with
              the App.
            </li>
            <li>
              Use bots, scrapers, scripts, or automated tools to interact with the App in ways that
              circumvent its intended functionality or harm other users.
            </li>
            <li>
              Remove, alter, or obscure any copyright, trademark, or other proprietary notices
              displayed in the App.
            </li>
            <li>
              Exploit vulnerabilities in the App or associated smart contracts for personal gain.
              Responsible disclosure to the development team is encouraged.
            </li>
          </ul>
        </SectionCard>

        <SectionCard title="3. Intellectual Property">
          <p>
            All content, design, code, graphics, logos, and user interface elements of SelfWorth
            DApp — excluding on-chain smart contract bytecode deployed to the public Solana network
            — are the intellectual property of the Licensor and are protected by applicable
            copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            Open-source components used within the App are subject to their respective licenses.
            Nothing in this EULA transfers any ownership of intellectual property to you. Your use
            of the App does not grant you any rights in our trademarks, service marks, or trade
            names.
          </p>
          <p>
            The SelfWorth DApp name, branding, and associated marks are proprietary. You may not
            use them without our prior written permission.
          </p>
        </SectionCard>

        <SectionCard title="4. Disclaimers">
          <p>
            SelfWorth DApp is provided{' '}
            <strong style={{ color: 'hsl(40 15% 88%)' }}>"as is"</strong> and{' '}
            <strong style={{ color: 'hsl(40 15% 88%)' }}>"as available"</strong> without warranties
            of any kind, express or implied, including but not limited to warranties of
            merchantability, fitness for a particular purpose, or non-infringement.
          </p>
          <p>
            We do not warrant that the App will be uninterrupted, error-free, or free from
            vulnerabilities. Blockchain transactions are irreversible; we are not responsible for
            losses arising from user error, network congestion, smart contract bugs, or wallet
            compromise.
          </p>
          <p>
            Nothing in this App constitutes financial, investment, legal, or tax advice. You assume
            full responsibility for your decisions to lock tokens, donate funds, or participate in
            prize pools. Crypto assets are volatile and participation may result in loss of funds.
          </p>
        </SectionCard>

        <SectionCard title="5. Limitation of Liability">
          <p>
            To the maximum extent permitted by applicable law, the Licensor and its affiliates,
            directors, officers, employees, and contributors shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages arising from your use of or
            inability to use SelfWorth DApp, including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Loss of tokens, funds, or digital assets of any kind.</li>
            <li>Loss of data, profits, goodwill, or business opportunities.</li>
            <li>Damages arising from smart contract interactions or third-party wallet failures.</li>
            <li>Any unauthorized access to or alteration of your transmissions or data.</li>
          </ul>
          <p>
            Our total liability to you for any claim arising from your use of the App shall not
            exceed the greater of (a) the amount you paid to use the App in the 12 months preceding
            the claim, or (b) USD $50.
          </p>
        </SectionCard>

        <SectionCard title="6. Termination">
          <p>
            This EULA is effective until terminated. Your rights under this EULA will terminate
            automatically, without notice, if you fail to comply with any of its terms. Upon
            termination, you must immediately cease all use of the App.
          </p>
          <p>
            We reserve the right to suspend or terminate access to SelfWorth DApp at any time, for
            any reason, with or without notice — including but not limited to suspected abuse,
            violations of this EULA, or operational decisions. On-chain assets you hold remain yours
            and are unaffected by App-level termination.
          </p>
          <p>
            Provisions of this EULA that by their nature should survive termination — including
            intellectual property rights, disclaimers, and limitation of liability — shall survive.
          </p>
        </SectionCard>

        {/* Footer note */}
        <div
          className="mt-8 pt-6 text-xs"
          style={{ borderTop: '1px solid hsl(35 12% 14%)', color: 'hsl(35 8% 42%)' }}
        >
          <p>
            This EULA constitutes the entire agreement between you and SelfWorth DApp with respect
            to the license granted herein. If any provision is found unenforceable, the remaining
            provisions remain in full effect. We reserve the right to update this EULA; continued
            use constitutes acceptance.
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
